import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import type { CreateLoanInput } from '@accounting/shared';
import { useCreateLoan } from '../../hooks/useLoans';
import { useAccounts } from '../../hooks/useAccounts';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { ApiError } from '../../lib/api';
import { LoanBuilder, type LoanBuilderValues } from '../../components/loans/LoanBuilder';

export const Route = createFileRoute('/_auth/loans/new')({
  component: NewLoanPage,
});

function NewLoanPage() {
  const companyId = useActiveCompanyId();
  const navigate = useNavigate();
  const { data: accounts = [] } = useAccounts(companyId);
  const create = useCreateLoan(companyId);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <p className="muted">No active company.</p>
        <Link to="/companies" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>Go to Companies</Link>
      </div>
    );
  }

  const errorMessage =
    create.error instanceof ApiError
      ? (create.error.body as { error?: string } | null)?.error ?? 'Could not create loan'
      : create.error?.message;

  const submit = (v: LoanBuilderValues) => {
    const input: CreateLoanInput = {
      name: v.name,
      lenderContactId: v.contactId ?? undefined,
      liabilityAccountId: v.principalAccountId,
      interestAccountId: v.interestAccountId,
      paymentAccountId: v.bankAccountId ?? undefined,
      autoPostPayments: v.autoPostPayments,
      funding: v.funding,
      principal: v.terms.principal,
      annualRatePct: v.terms.annualRatePct,
      rateBasis: v.terms.rateBasis,
      paymentFrequency: v.terms.paymentFrequency,
      amortizationPeriods: v.terms.amortizationPeriods,
      numberOfPayments: v.terms.numberOfPayments,
      paymentAmount: v.terms.paymentAmount ?? undefined,
      originationDate: v.terms.originationDate,
      firstPaymentDate: v.terms.firstPaymentDate,
      businessDayRule: v.terms.businessDayRule,
      lateChargePct: v.terms.lateChargePct ?? undefined,
      lateChargeGraceDays: v.terms.lateChargeGraceDays ?? undefined,
      defaultRateSpreadPct: v.terms.defaultRateSpreadPct ?? undefined,
    };
    create.mutate(input, { onSuccess: (l) => void navigate({ to: '/loans/$id', params: { id: l.id } }) });
  };

  return (
    <div>
      <Link to="/loans" style={{ fontSize: 13 }}>← Loans</Link>
      <h1 style={{ fontSize: 24, margin: '8px 0 4px' }}>New loan</h1>
      <p className="muted" style={{ margin: '0 0 16px', fontSize: 13 }}>
        A note the company owes. Describe the terms as the agreement states them; the schedule, the balloon and the payment entries follow from those.
      </p>
      <LoanBuilder
        side="payable"
        companyId={companyId}
        accounts={accounts}
        onSubmit={submit}
        isPending={create.isPending}
        errorMessage={errorMessage}
        cancelHref="/loans"
      />
    </div>
  );
}
